import { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ClipboardList, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { fetchInspectionReference, fetchWatchInspection, saveWatchInspection, createInspectionOption } from "@/lib/api";
import { errorMessage } from "@/lib/errors";

const GROUPS = ["Inspection", "Running", "Complications"];
const SENTINEL_NONE = "__none__";

/**
 * Watch inspection sheet — slides over the Watch Detail page.
 *
 * Per user spec:
 *   - Single-select result per category (dropdown)
 *   - Single-select action per category (dropdown; action options start empty
 *     and are populated by the user via the Inspection Setup page)
 *   - Free-text reading inputs for Rate / Amplitude / Beat Error
 *   - Per-item notes
 *
 * Stores option_id (never label) so renaming an option in setup propagates
 * everywhere instantly.
 */
export default function InspectionSheet({ open, onOpenChange, watch, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reference, setReference] = useState({ categories: [] });
  const [itemsByCat, setItemsByCat] = useState({});
  const [summary, setSummary] = useState("");
  const [performedBy, setPerformedBy] = useState("");
  const [performedAt, setPerformedAt] = useState(null);

  useEffect(() => {
    if (!open || !watch?.id) return;
    setLoading(true);
    Promise.all([fetchInspectionReference(), fetchWatchInspection(watch.id)])
      .then(([ref, current]) => {
        setReference(ref || { categories: [] });
        const insp = current?.inspection;
        const map = {};
        for (const it of insp?.items || []) {
          map[it.category_id] = {
            result_option_id: it.result_option_id || "",
            action_option_id: it.action_option_id || "",
            reading: it.reading || "",
            notes: it.notes || "",
          };
        }
        setItemsByCat(map);
        setSummary(insp?.summary_notes || "");
        setPerformedBy(insp?.performed_by || "");
        setPerformedAt(insp?.performed_at || null);
      })
      .catch((err) => toast.error(errorMessage(err, "Could not load inspection")))
      .finally(() => setLoading(false));
  }, [open, watch?.id]);

  // Reload just the reference (categories + options) after adding an action
  // inline from a row, so the new option appears in the Select immediately.
  const reloadReference = async () => {
    try {
      const ref = await fetchInspectionReference();
      setReference(ref || { categories: [] });
    } catch (err) {
      toast.error(errorMessage(err, "Could not reload"));
    }
  };

  const setItem = (catId, patch) => {
    setItemsByCat((prev) => ({
      ...prev,
      [catId]: { ...(prev[catId] || {}), ...patch },
    }));
  };

  const grouped = useMemo(() => {
    const out = Object.fromEntries(GROUPS.map((g) => [g, []]));
    for (const c of reference.categories || []) {
      const g = GROUPS.includes(c.group) ? c.group : "Inspection";
      out[g].push(c);
    }
    return out;
  }, [reference]);

  const onSave = async () => {
    if (!watch?.id) return;
    setSaving(true);
    try {
      const items = (reference.categories || [])
        .map((c) => {
          const v = itemsByCat[c.id] || {};
          if (!v.result_option_id && !v.action_option_id && !v.reading && !v.notes) {
            return null;
          }
          return {
            category_id: c.id,
            result_option_id: v.result_option_id || null,
            action_option_id: v.action_option_id || null,
            reading: v.reading || null,
            notes: v.notes || null,
          };
        })
        .filter(Boolean);
      await saveWatchInspection(watch.id, {
        performed_by: performedBy || null,
        summary_notes: summary || null,
        items,
      });
      toast.success("Inspection saved");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(errorMessage(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl bg-[#FBF3D9] border-l border-[#8B6A1F]/35 overflow-y-auto p-6"
        data-testid="inspection-sheet"
      >
        <SheetHeader className="text-left mb-4">
          <SheetTitle className="font-serif-display text-3xl text-[#0A0604] flex items-center gap-2">
            <ClipboardList size={20} /> Inspection
          </SheetTitle>
          <SheetDescription className="text-xs text-[#3A2A10]">
            #{watch?.excel_id} · {watch?.brand} {watch?.caliber}
            {performedAt && (
              <> · last saved {new Date(performedAt).toLocaleDateString()}</>
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="flex items-center justify-between mb-4 text-[10px] uppercase tracking-[0.2em] text-[#3A2A10]">
          <Link
            to="/data/inspection"
            className="inline-flex items-center gap-1 hover:text-[#0A0604] transition-colors"
            data-testid="inspection-setup-link"
          >
            <Settings size={11} /> Manage categories &amp; actions
          </Link>
        </div>

        {loading ? (
          <div className="py-20 flex items-center justify-center text-sm text-[#3A2A10] gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 pb-4 border-b border-[#8B6A1F]/25">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10]">Performed by</Label>
                <Input
                  value={performedBy}
                  onChange={(e) => setPerformedBy(e.target.value)}
                  placeholder="Self / Watchmaker name"
                  className="dark-input mt-1"
                  data-testid="inspection-performed-by"
                />
              </div>
            </div>

            {GROUPS.map((g) => {
              const cats = grouped[g] || [];
              if (!cats.length) return null;
              return (
                <section key={g} className="mb-6">
                  <h3 className="font-serif-display text-xl text-[#0A0604] mb-3">{g}</h3>
                  <div className="space-y-3">
                    {cats.map((c) => (
                      <CategoryRow
                        key={c.id}
                        category={c}
                        value={itemsByCat[c.id] || {}}
                        onChange={(patch) => setItem(c.id, patch)}
                        onActionAdded={reloadReference}
                      />
                    ))}
                  </div>
                </section>
              );
            })}

            <div className="mt-4 pt-4 border-t border-[#8B6A1F]/25">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10]">Summary notes</Label>
              <Textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="Overall inspection summary, recommendations, what to quote the customer…"
                className="dark-input mt-1 min-h-32 resize-y"
                data-testid="inspection-summary"
              />
            </div>

            <div className="sticky bottom-0 -mx-6 mt-6 px-6 py-3 bg-[#FBF3D9] border-t border-[#8B6A1F]/35 flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className="text-[#3A2A10]"
              >
                Cancel
              </Button>
              <Button
                onClick={onSave}
                disabled={saving}
                className="bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10]"
                data-testid="inspection-save"
              >
                {saving ? "Saving…" : "Save inspection"}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function CategoryRow({ category, value, onChange, onActionAdded }) {
  return (
    <div className="bg-[#F9F0D4] border border-[#8B6A1F]/30 rounded-sm p-3" data-testid={`inspection-row-${category.id}`}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-sm font-medium text-[#0A0604]">{category.name}</div>
      </div>
      {category.kind === "reading" ? (
        <Input
          value={value.reading || ""}
          onChange={(e) => onChange({ reading: e.target.value })}
          placeholder={category.name === "Beat Error" ? "ms" : category.name === "Amplitude" ? "°" : "s/day"}
          className="dark-input"
          data-testid={`inspection-reading-${category.id}`}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <Label className="text-[9px] uppercase tracking-wider text-[#3A2A10] mb-1 block">Result</Label>
            <OptionSelect
              value={value.result_option_id || ""}
              onChange={(v) => onChange({ result_option_id: v })}
              options={category.result_options || []}
              testid={`inspection-result-${category.id}`}
              placeholder="Pick result…"
            />
          </div>
          <div>
            <Label className="text-[9px] uppercase tracking-wider text-[#3A2A10] mb-1 block">Action</Label>
            {category.action_options?.length ? (
              <OptionSelect
                value={value.action_option_id || ""}
                onChange={(v) => onChange({ action_option_id: v })}
                options={category.action_options}
                testid={`inspection-action-${category.id}`}
                placeholder="Pick action…"
              />
            ) : (
              <InlineActionAdder
                categoryId={category.id}
                onAdded={async (newOpt) => {
                  // Refresh the reference so the new option appears in the
                  // dropdown, then auto-select it for this row.
                  await onActionAdded();
                  if (newOpt?.id) onChange({ action_option_id: newOpt.id });
                }}
                testid={`inspection-action-add-${category.id}`}
              />
            )}
          </div>
        </div>
      )}
      <Input
        value={value.notes || ""}
        onChange={(e) => onChange({ notes: e.target.value })}
        placeholder="notes (optional)"
        className="dark-input mt-2 text-xs"
        data-testid={`inspection-notes-${category.id}`}
      />
    </div>
  );
}

function InlineActionAdder({ categoryId, onAdded, testid }) {
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault?.();
    const v = label.trim();
    if (!v) return;
    setBusy(true);
    try {
      const opt = await createInspectionOption(categoryId, { label: v, kind: "action" });
      setLabel("");
      onAdded(opt);
    } catch (err) {
      toast.error(errorMessage(err, "Add failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex gap-1.5" data-testid={testid}>
      <Input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Type new action then ↵"
        className="dark-input h-9 text-sm flex-1"
        disabled={busy}
      />
      <Button
        type="submit"
        size="sm"
        className="h-9 px-2 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10]"
        disabled={!label.trim() || busy}
        title="Add this action"
      >
        +
      </Button>
    </form>
  );
}

function OptionSelect({ value, onChange, options, testid, placeholder, disabled }) {
  return (
    <Select
      value={value || SENTINEL_NONE}
      onValueChange={(v) => onChange(v === SENTINEL_NONE ? "" : v)}
      disabled={disabled}
    >
      <SelectTrigger className="dark-input h-9 text-sm" data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-[#F9F0D4] border-[#8B6A1F]/35">
        <SelectItem value={SENTINEL_NONE} className="text-xs italic text-[#3A2A10]">—</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.id} value={o.id} className="text-sm">
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
